import { Clipboard } from '@angular/cdk/clipboard'
import { Component, Input, TemplateRef } from '@angular/core'
import { DateFormat, MomentConvertible } from '../../util'
import { ApiResult } from '../remote-data'
import { ToastService } from '../toast'
import { TooltipAlignment, TooltipDisplay, TooltipPosition } from '../tooltip'
import { TableAction } from './table-action'
import { BadgeDetails, IconDetails, TextTruncate, TooltipDetails } from './table-column'
import { VerticalBreakpoint } from './table.component'

export type CellType = 'date' | 'text' | 'link' | 'badge' | 'action' | 'template'

// All cells support an icon and truncation of long text
export interface BaseCellData {
  icon?: IconDetails | undefined
  truncation?: TextTruncate | undefined
}

export interface TextCellData extends BaseCellData {
  data: string
  canCopy: boolean
  tooltip?: TooltipDetails | undefined
}

export interface BadgeCellData extends BaseCellData {
  data: BadgeDetails
}

export interface LinkCellData extends BaseCellData {
  data: string
  routerLink: string[]
}

export interface DateTimeCellData extends BaseCellData {
  data: MomentConvertible | undefined
  format: DateFormat
  titleFormat?: DateFormat | undefined
}

export interface ActionsCellData extends BaseCellData {
  data: any
  actions: TableAction<any, any>[]
}

export interface TemplateCellData extends BaseCellData {
  data: any
  template: TemplateRef<any>
}

export type CellData =
  | TextCellData
  | LinkCellData
  | DateTimeCellData
  | BadgeCellData
  | ActionsCellData
  | TemplateCellData

@Component({
  selector: 'ftr-table-cell',
  templateUrl: './table-cell.component.html',
  styleUrls: ['./table-cell.component.css'],
})
export class TableCellComponent {
  readonly tooltipPosition = TooltipPosition
  readonly tooltipAlignment = TooltipAlignment
  readonly tooltipDisplay = TooltipDisplay

  @Input()
  cell: CellData

  @Input()
  cellType: CellType

  @Input()
  columnName: string

  /**
   * At what point we switch to vertical cards, rather than horizontal rows
   */
  @Input()
  verticalBreakpoint: VerticalBreakpoint = 'small'

  constructor(
    private readonly clipboard: Clipboard,
    private readonly toastService: ToastService,
  ) {}

  createActionFunction<T, R>(f: (t: T) => ApiResult<R>): () => ApiResult<R> {
    return () => f(this.cell.data)
  }

  asTextCell(cell: CellData): TextCellData {
    return cell as TextCellData
  }

  asLinkCell(cell: CellData): LinkCellData {
    return cell as LinkCellData
  }

  asActionCell(cell: CellData): ActionsCellData {
    return cell as ActionsCellData
  }

  asTemplateCell(cell: CellData): TemplateCellData {
    return cell as TemplateCellData
  }

  asDateCell(cell: CellData): Required<DateTimeCellData> {
    const c = cell as DateTimeCellData
    if (!c.titleFormat) {
      c.titleFormat = c.format
    }
    return c as Required<DateTimeCellData>
  }

  asBadgeCell(cell: CellData): BadgeCellData {
    return cell as BadgeCellData
  }

  copyToClipboard(text: string, $event: Event): void {
    $event.preventDefault()
    $event.stopPropagation()
    this.clipboard.copy(text)
    this.toastService.info(`${this.columnName} copied to clipboard`)
  }

  trackActionBy(index: number): any {
    return index
  }

  validateForceDisplayState(
    forceDisplayState: TooltipDisplay | undefined,
    message: string | undefined,
  ): TooltipDisplay | undefined {
    if (forceDisplayState) {
      return forceDisplayState
    }

    return message ? undefined : this.tooltipDisplay.Hide
  }
}
